import { UserDto } from './../../../models/dto/user.dto';
import { AuthService } from './../../../Services/auth.service';
import { CommunicationService } from './../../../Services/communication.service';
import { FileName } from '../../../models/file-name';
import { HelperService } from '../../../Services/helper.service';
import { ConnectorRequestStateEnum } from '../../../models/enum/connector-request-state.enum';
import { HttpService } from '../../../Services/http.service';
import { AnalysisRequestsTableService } from '../../../Services/analysis-requests-table.service';
import { AnalysisRequestDto } from '../../../models/dto/analysis-request.dto';
import { DataSource } from '@angular/cdk/collections';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { PaginationResult } from 'ng-lightquery';
import { Subject, Subscription, interval } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { InfoModalComponent } from '../../modals/info-modal/info-modal.component';
import moment from 'moment';
import { PatientIdModalComponent } from '../../modals/patient-id-modal/patient-id-modal.component';
import { finalize } from 'rxjs/operators';
import { HttpEventType, HttpStatusCode } from '@angular/common/http';
import { DownloadingModalComponent } from '../../modals/downloading-modal/downloading-modal.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { Constants } from '../../../../constants';
import { getDefaultCalendarConfig, localizeDatePicker } from '../../../helpers/translateCalendar';


@Component({
  selector: 'app-requests-table',
  templateUrl: './requests-table.component.html',
  styleUrls: ['./requests-table.component.css'],
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'paginator-select-panel' },
    }
  ]
})
export class RequestsTableComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  sortedData = new MatTableDataSource();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(InfoModalComponent) infoModal: InfoModalComponent;
  @ViewChild(PatientIdModalComponent) patientIdModal: PatientIdModalComponent;
  @ViewChild(DownloadingModalComponent) downloadModal: DownloadingModalComponent;
  @ViewChild('fileUpload') fileUploadElement: ElementRef;
  @ViewChild('folderUpload') folderUploadElement: ElementRef;

  uploadFiles: File[] = [];
  patientId: string;
  dataSource: DataSource<AnalysisRequestDto>;
  usersPaginated: PaginationResult<AnalysisRequestDto>;
  tableOrderHeader = [
    'patientId',
    'created',
    'modified',
    'state',
    'analysis',
    'ordering',
    'source',
    'fileName',
    'lastViewed'
  ];

  urlParameters: Params;
  sourceValue: string;
  orderingValue: string;
  patientIdValue: string;
  organizationIdValue: string;

  searchByOrder$ = new Subject<string>();
  searchBySource$ = new Subject<string>();
  searchByState$ = new Subject<string>();
  searchByAnalysis$ = new Subject<string>();
  searchByViewed$ = new Subject<string>();
  searchByPatientId$ = new Subject<string>();
  searchByReqStartDate$ = new Subject<string>();
  searchByReqEndDate$ = new Subject<string>();
  searchByCompStartDate$ = new Subject<string>();
  searchByCompEndDate$ = new Subject<string>();
  searchByOrganizationId$ = new Subject<string>();


  usersPaginatedSubscription: Subscription;
  refreshPageSubscription: Subscription;
  refreshSource = interval(Constants.refreshRequestsInterval * 1000);
  searchItems: any;
  sortItems: any;
  statesList: any[] = [];
  analysisList: any[] = [];
  viewedList: any[] = [];
  selectedByDefaultStates = '';
  selectedByDefaultAnalysis = '';
  selectedByDefaultViewed = '';
  expanded = false;
  locale = getDefaultCalendarConfig();

  requestedDateRange: { startDate: moment.Moment, endDate: moment.Moment };
  completedDateRange: { startDate: moment.Moment, endDate: moment.Moment };
  model: any;
  isRotateSpin = false;
  rowId: string;
  isUploading = false;
  profile: UserDto;

  constructor(
    private translate: TranslateService,
    public tableService: AnalysisRequestsTableService,
    private http: HttpService,
    public helper: HelperService,
    private communication: CommunicationService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService
  ) {
    this.getProfile().then(() => {
      this.locale = localizeDatePicker(this.translate);
    });
  }

  @HostListener('window:beforeunload', ['$event']) // not allow refresh page until uploading in progress
  onBeforeUnload() {
    return !this.isUploading;
  }

  getParamsData() {
    this.route.queryParams.subscribe(
      (queryParam: any) => {
        this.sourceValue = this.helper.getValueFromParams(queryParam, 'source', this.searchBySource$);
        this.orderingValue = this.helper.getValueFromParams(queryParam, 'ordering', this.searchByOrder$);
        this.selectedByDefaultStates = this.helper.getValueFromParams(queryParam, 'states', this.searchByState$);
        this.selectedByDefaultViewed = this.helper.getValueFromParams(queryParam, 'viewed', this.searchByViewed$);
        this.patientIdValue = this.helper.getValueFromParams(queryParam, 'patientId', this.searchByPatientId$)
        this.selectedByDefaultAnalysis = this.helper.getValueFromParams(queryParam, 'analysisId', this.searchByAnalysis$);

        const reqStartDate = this.helper.getValueFromParams(queryParam, 'reqStartDate', this.searchByReqStartDate$);
        const reqEndDate = this.helper.getValueFromParams(queryParam, 'reqEndDate', this.searchByReqEndDate$);
        this.requestedDateRange = reqStartDate && reqStartDate ? { startDate: moment(reqStartDate), endDate: moment(reqEndDate) } : undefined;

        const completeStartDate = this.helper.getValueFromParams(queryParam, 'completeStartDate', this.searchByCompStartDate$);
        const completeEndDate = this.helper.getValueFromParams(queryParam, 'completeEndDate', this.searchByCompEndDate$);
        this.completedDateRange = completeStartDate && completeEndDate ? { startDate: moment(completeStartDate), endDate: moment(completeEndDate) } : undefined;

        if (this.helper.isPortalAdminOrPortalUserPermission(this.profile)) {
          this.organizationIdValue = this.helper.getValueFromParams(queryParam, 'organizationId', this.searchByOrganizationId$)
        } else {
          this.organizationIdValue = null;
        }
      }
    );
  }

  async ngOnInit(): Promise<void> {
    await this.getSelectsItems();
    this.bindSearchValues();

    this.urlParameters = { ...this.route.snapshot.queryParams };

    if (this.urlParameters) {
      const keys = Object.keys(this.urlParameters);
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        this.tableService.setQueryParameter(key, this.urlParameters[key]);
      }
    }

    this.dataSource = this.tableService;

    this.getParamsData();
    this.usersPaginatedSubscription = this.tableService.paginationResult.subscribe(r => this.usersPaginated = r);

    this.refreshPageSubscription = this.refreshSource.subscribe(() => {
      this.tableService.forceRefresh();
    })

    this.sortedData.sort = this.sort;
    this.sort.sort({ id: 'created', start: 'desc', disableClear: true });
    this.sort.disableClear = true;
  }

  async getProfile() {
    this.profile = await this.authService.getUserProfile();
  }

  bindSearchValues() {
    this.helper.searchByFields('source', this.searchBySource$, this.tableService);
    this.helper.searchByFields('ordering', this.searchByOrder$, this.tableService);
    this.helper.searchByFields('states', this.searchByState$, this.tableService);
    this.helper.searchByFields('analysisId', this.searchByAnalysis$, this.tableService);
    this.helper.searchByFields('viewed', this.searchByViewed$, this.tableService);
    this.helper.searchByFields('patientId', this.searchByPatientId$, this.tableService);
    this.helper.searchByFields('reqStartDate', this.searchByReqStartDate$, this.tableService);
    this.helper.searchByFields('reqEndDate', this.searchByReqEndDate$, this.tableService);
    this.helper.searchByFields('completeStartDate', this.searchByCompStartDate$, this.tableService);
    this.helper.searchByFields('completeEndDate', this.searchByCompEndDate$, this.tableService);
    this.helper.searchByFields('organizationId', this.searchByOrganizationId$, this.tableService);
  }

  getSelectsItems() {
    this.getControlsItems();
    this.translate.onLangChange.subscribe(() => {
      this.getControlsItems();
    });
  }

  getControlsItems() {
    this.statesList = this.getControlStateItems();
    this.viewedList = this.getControlViewItems();
    this.getControlAnalysisItems()
      .then((items) => this.analysisList = items)
      .catch((): any[] => this.analysisList = []);
  }

  async getControlAnalysisItems() {
    const items = [
      { id: '', name: this.translate.instant('CONSTANTS.ALL') }
    ];
    const analysisTypes = await this.http.getAnalysisTypesForUser();
    for (const analysis of analysisTypes) {
      items.push(
        {
          id: analysis.id,
          name: this.translate.instant(`REQUEST_TABLE.${analysis.name}`)
        });
    }
    return items;
  }

  getControlStateItems() {
    const items = [
      { id: '', name: this.translate.instant('CONSTANTS.ALL') },
      { id: ConnectorRequestStateEnum.pending, name: this.translate.instant('CONSTANTS.PENDING_STATE') },
      { id: ConnectorRequestStateEnum.completed, name: this.translate.instant('CONSTANTS.COMPLETED_STATE') },
      { id: ConnectorRequestStateEnum.failed, name: this.translate.instant('CONSTANTS.FAILED_STATE') }
    ];
    return items;
  }

  getControlViewItems() {
    const items = [
      { id: '', name: this.translate.instant('CONSTANTS.ALL') },
      { id: 'true', name: this.translate.instant('CONSTANTS.VIEWED') },
      { id: 'false', name: this.translate.instant('CONSTANTS.NOT_VIEWED') },
    ];
    return items;
  }

  applyFilterByAnalysis() {
    this.helper.setParamsAndUpdate([this.selectedByDefaultAnalysis], ['analysisId'], this.urlParameters, this.router, this.route);
  }

  applyFilterByOrdering() {
    this.helper.setParamsAndUpdate([this.orderingValue], ['ordering'], this.urlParameters, this.router, this.route);
  }

  applyFilterBySource() {
    this.helper.setParamsAndUpdate([this.sourceValue], ['source'], this.urlParameters, this.router, this.route);
  }

  applyFilterByState() {
    this.helper.setParamsAndUpdate([this.selectedByDefaultStates], ['states'], this.urlParameters, this.router, this.route);
  }

  applyFilterByViewed() {
    this.helper.setParamsAndUpdate([this.selectedByDefaultViewed], ['viewed'], this.urlParameters, this.router, this.route);
  }

  applyFilterByPatientId() {
    this.helper.setParamsAndUpdate([this.patientIdValue], ['patientId'], this.urlParameters, this.router, this.route);
  }

  applyFilterByOrganizationId(organizationId: string) {
    if (!this.urlParameters) {
      this.urlParameters = Object.assign({}, this.route.snapshot.queryParams);
    }
    this.organizationIdValue = organizationId;
    this.helper.setParamsAndUpdate([this.organizationIdValue], ['organizationId'], this.urlParameters, this.router, this.route);
  }

  applyFilterByReqDate() {
    const startDate = this.requestedDateRange?.startDate ? this.requestedDateRange.startDate.toISOString() : '';
    const entDate = this.requestedDateRange?.endDate ? this.requestedDateRange.endDate.toISOString() : '';
    this.helper.setParamsAndUpdate([startDate, entDate], ['reqStartDate', 'reqEndDate'], this.urlParameters, this.router, this.route);
  }

  applyFilterByCompDate() {
    const startDate = this.completedDateRange?.startDate ? this.completedDateRange.startDate.toISOString() : '';
    const entDate = this.completedDateRange?.endDate ? this.completedDateRange.endDate.toISOString() : '';
    this.helper.setParamsAndUpdate([startDate, entDate], ['completeStartDate', 'completeEndDate'], this.urlParameters, this.router, this.route);
  }

  clickFileUploadEvent() {
    this.fileUploadElement.nativeElement.click();
  }

  clickFolderUploadEvent() {
    this.folderUploadElement.nativeElement.click();
  }

  confirmFileName(fileName: FileName) {
    this.http.createAnalysisRequest(this.uploadFiles, fileName).pipe(
      finalize(() => {
        this.resetUploadStatus();
      })
    ).subscribe((event) => {
      if (event.type == HttpEventType.UploadProgress) {
        const uploadProgress = Math.round(100 * (event.loaded / event.total));
        this.communication.sendUploadProgress(uploadProgress);
        this.isUploading = true;
      }
      if (event.body && event.status === HttpStatusCode.Created) {
        this.showSuccessUploadModal();
        this.tableService.forceRefresh();
      }
    }, () => {
      this.showErrorModal();
    });
  }

  async fileAdd(event: any) {
    const files = Array.from(event.target.files as FileList) as File[];
    if (files.length === 0) {
      return;
    }
    this.uploadFiles = files;

    // Folder selections set webkitRelativePath (e.g. "Patient123/slice001.dcm");
    // for single-file selections it's empty.
    const isFolder = !!files[0].webkitRelativePath;
    const displayName = isFolder
      ? files[0].webkitRelativePath.split('/')[0]
      : files[0].name;

    const analysisTypes = await this.http.getAnalysisTypesForUser();
    this.patientIdModal.open(displayName, analysisTypes, isFolder);
    event.target.value = '';
  }

  resetUploadStatus() {
    this.isUploading = false;
    this.communication.sendUploadProgress(null);
  }

  changeArrowState() {
    this.expanded = !this.expanded;
  }

  isCompletedState(state: ConnectorRequestStateEnum) {
    return state === ConnectorRequestStateEnum.completed;
  }

  isPendingState(state: ConnectorRequestStateEnum) {
    return state === ConnectorRequestStateEnum.pending;
  }

  isFailedState(state: ConnectorRequestStateEnum) {
    return state === ConnectorRequestStateEnum.failed;
  }

  getTranslateState(state: ConnectorRequestStateEnum) {
    switch (state) {
      case ConnectorRequestStateEnum.completed: return this.translate.instant('CONSTANTS.COMPLETED_STATE');
      case ConnectorRequestStateEnum.pending: return this.translate.instant('CONSTANTS.PENDING_STATE');
      case ConnectorRequestStateEnum.failed: return this.translate.instant('CONSTANTS.FAILED_STATE');
    }
  }

  async getReportFile(id: string) {
    this.downloadModal.open();
    this.helper.downloadReportFile(id).catch(async () => {
      await this.showErrorModal();
    }).finally(() => {
      this.downloadModal.close();
    });
  }

  async retriggerRequest(id: string) {
    this.isRotateSpin = true;
    this.rowId = id;
    try {
      await this.http.retriggerRequest(id).toPromise();
      this.updateTableAfterRetrigger();
    } catch {
      this.showRetriggerError();
    }
  }

  updateTableAfterRetrigger() {
    const updateTimeout = setTimeout(async () => {
      this.isRotateSpin = false;
      await this.showSuccessRetriggerModal();
      this.tableService.forceRefresh();
      clearTimeout(updateTimeout);
    }, Constants.updateTableAfterRetriggerTimeOut);
  }

  showRetriggerError() {
    const errorTimeout = setTimeout(async () => {
      this.isRotateSpin = false;
      clearTimeout(errorTimeout);
      await this.showErrorModal();
    }, Constants.updateTableAfterRetriggerTimeOut);
  }

  async showSuccessUploadModal() {
    const modalMsg = this.translate.instant('REQUEST_TABLE.SUCCESS_UPLOAD_MODAL');
    await this.infoModal.open(modalMsg, null, true);
  }

  async showSuccessRetriggerModal() {
    const modalMsg = this.translate.instant('REQUEST_TABLE.SUCCESS_RE_TRIGGER_MODAL');
    await this.infoModal.open(modalMsg, null, true);
  }

  async showErrorModal() {
    const modalMsg = this.translate.instant('REQUEST_TABLE.ERROR_MODAL');
    await this.infoModal.open(modalMsg, null, false);
  }

  ngOnDestroy() {
    this.usersPaginatedSubscription.unsubscribe();
    this.searchByPatientId$.unsubscribe();
    this.searchBySource$.unsubscribe();
    this.searchByState$.unsubscribe();
    this.searchByViewed$.unsubscribe();
    this.searchByReqStartDate$.unsubscribe();
    this.searchByReqEndDate$.unsubscribe();
    this.searchByCompStartDate$.unsubscribe();
    this.searchByCompEndDate$.unsubscribe();
    this.searchByOrganizationId$.unsubscribe();
    this.refreshPageSubscription.unsubscribe();
  }
}
